/**
 * Zentrale Icon-Whitelist fuer dynamische Lucide-Aufloesungen (Joerg 2026-05-27).
 *
 * Hintergrund: An Stellen wie `MobileHomeTabBar`, `OverviewTabBar`, `PortalShell`,
 * `HomePage`, `LoginPage`, `MobileHomepageAdminPage` werden Icons ueber einen
 * Namen aus DB/Backend-Config aufgeloest. Vor diesem Refactor lief das ueber
 * `import * as Icons from 'lucide-react'` + `Icons[name]` — was Tree-Shaking
 * blockiert hat und das gesamte ~800-Icon-Package ins Bundle gezogen hat
 * (~700 KB).
 *
 * Diese Datei pflegt eine explizite Whitelist aller Icon-Namen, die irgendwo
 * dynamisch aufgeloest werden koennen. Tree-Shaking funktioniert damit normal
 * und der `icons`-Vendor-Chunk schrumpft auf das, was wir wirklich nutzen.
 *
 * **Wenn ein neuer Icon-Name in einer DB-Spalte (z.B. portal_app_modules.icon,
 * portal_departments.icon, portal_user_pages.icon, portal_page_templates.icon)
 * oder Backend-Konfig (TAB_DEFINITIONS.icon, HOME_MOBILE_DEFS.icon) auftaucht,
 * MUSS er hier ergaenzt werden — sonst rendert die App den Fallback.**
 *
 * Quellen aktueller Eintraege (Stand 2026-05-27):
 *   - portal_app_modules.icon  : BarChart3, Clock, Database, FileText, Folder,
 *                                HardHat, LayoutGrid, LayoutTemplate, Library
 *   - portal_departments.icon  : (gleicher Pool — `Folder` als Default)
 *   - TAB_DEFINITIONS.icon     : BarChart3, Calendar, CheckSquare, Clock, FileText,
 *                                Home, LayoutDashboard, Library, Package, Settings,
 *                                UserCog, Users
 *   - HOME_MOBILE_DEFS.icon    : Bot, Clock, HardHat
 *   - PortalShell.TenantIcon   : Tenant-Branding, Default Shield
 *   - HomePage.dept-fallback   : Folder, User, Shield
 *   - LoginPage.tenant-icon    : Shield-Default
 *   - MobileHomepageAdminPage  : analog TAB-Slugs
 */
import {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  BookOpen,
  Bot,
  Box,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  Car,
  CheckSquare,
  Circle,
  CircleDollarSign,
  ClipboardList,
  Clock,
  Contact,
  Database,
  FileText,
  FlaskConical,
  Folder,
  FolderOpen,
  FolderTree,
  GraduationCap,
  HardHat,
  Home,
  Inbox,
  Layout as LayoutIcon,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Library,
  Map as MapIcon,
  MapPin,
  LifeBuoy,
  MessageCircle,
  Mic,
  NotebookPen,
  Package,
  Plane,
  Receipt,
  Settings,
  Shield,
  ShieldCheck,
  Smartphone,
  Sparkles,
  Stethoscope,
  User,
  UserCog,
  Users,
  Wrench,
} from 'lucide-react';

type IconComponent = React.ComponentType<{ size?: number; className?: string }>;

export const ICON_REGISTRY: Record<string, IconComponent> = {
  AlertCircle,
  AlertTriangle,
  BarChart3,
  BookOpen,
  Bot,
  Box,
  Briefcase,
  Building2,
  Calculator,
  Calendar,
  Car,
  CheckSquare,
  Circle,
  CircleDollarSign,
  Contact,
  GraduationCap,
  Plane,
  Sparkles,
  Stethoscope,
  ClipboardList,
  Clock,
  Database,
  FileText,
  FlaskConical,
  Folder,
  FolderOpen,
  FolderTree,
  HardHat,
  Home,
  Inbox,
  Layout: LayoutIcon,
  LayoutDashboard,
  LayoutGrid,
  LayoutTemplate,
  Library,
  LifeBuoy,
  Map: MapIcon,
  MapPin,
  MessageCircle,
  Mic,
  NotebookPen,
  Package,
  Receipt,
  Settings,
  Shield,
  ShieldCheck,
  Smartphone,
  User,
  UserCog,
  Users,
  Wrench,
};

/**
 * Generische Lookup-Komponente. Rendert das per Name nachgeschlagene Icon
 * oder — falls Name nicht in der Whitelist steht — das `fallback`-Icon.
 *
 * Bei Treffer ausserhalb der Whitelist wird in DEV ein Console-Warn-Hinweis
 * geloggt, damit der Drift zwischen DB/Config und Whitelist nicht unbemerkt
 * bleibt.
 */
export function LucideByName({
  name,
  fallback: Fallback,
  size = 16,
  className = '',
}: {
  name?: string | null;
  fallback: IconComponent;
  size?: number;
  className?: string;
}) {
  const Resolved = name ? ICON_REGISTRY[name] : undefined;
  // Nur Dev-Warn — vite exposiert import.meta.env, tsc im Package kennt es
  // nicht (kein vite/client-Types-Include). `any`-Cast statt tsconfig-Umbau.
  if (name && !Resolved && (import.meta as { env?: { DEV?: boolean } }).env?.DEV) {
    // eslint-disable-next-line no-console
    console.warn(`[icon-registry] Unbekannter Icon-Name "${name}" — Whitelist ergaenzen in src/lib/icon-registry.ts.`);
  }
  const Icon = Resolved ?? Fallback;
  return <Icon size={size} className={className} />;
}
